import { ReactNode } from 'react';
import styled from 'styled-components';
import { colors, s16, s32, s8, zIndex } from '@ardoq/design-tokens';
import { header2 } from '@ardoq/typography';
import { FlexBox, FlexBoxProps } from '@ardoq/layout';

type PageHeaderProps = {
  title?: ReactNode;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  children?: ReactNode;
  skipVerticalPadding?: boolean;
} & Pick<FlexBoxProps, 'justify' | 'align' | 'gap'>;

const PageHeaderWrapper = styled(FlexBox).attrs<
  {
    $skipVerticalPadding?: boolean;
  } & Pick<FlexBoxProps, 'justify' | 'align' | 'gap'>
>(({ align = 'start', justify = 'space-between', gap = 'medium' }) => ({
  align,
  justify,
  gap,
}))<{ $skipVerticalPadding?: boolean }>`
  /* We want to make the header constant height
     regardless we have or not the header buttons
     52px = 36px of button + 2 x 8px padding */
  min-height: 52px;
  border-bottom: 1px solid ${colors.borderAction};
  background-color: ${colors.surfaceDefault};
  padding: ${({ $skipVerticalPadding }) =>
    $skipVerticalPadding ? `0 ${s16}` : `${s8} ${s32}`};
  z-index: ${zIndex.PAGE_HEADER};
`;

const TitleWrapper = styled.h1`
  ${header2};
  color: ${colors.textDefault};
  margin: 0;
  display: flex;
  /* button height */
  align-items: center;
  min-height: 36px;
`;

export const PageHeader = ({
  title,
  leftContent,
  rightContent,
  children,
  skipVerticalPadding,
  ...flexBoxProps
}: PageHeaderProps) => {
  return (
    <PageHeaderWrapper
      $skipVerticalPadding={skipVerticalPadding}
      {...flexBoxProps}
    >
      {(leftContent || title) && (
        <FlexBox gap="small">
          {title && <TitleWrapper>{title}</TitleWrapper>}
          {leftContent}
        </FlexBox>
      )}
      {rightContent && <div>{rightContent}</div>}
      {children}
    </PageHeaderWrapper>
  );
};
